/**
 * MongoDB duplicate-key (E11000) detection + friendly messaging.
 *
 * A unique-index violation is NOT a structured `validation_error`; it surfaces
 * on the GENERIC failure path — REST `409`/`500` with a raw
 * `E11000 duplicate key error ... dup key: { name: "x" }` message, or a
 * WebSocket `{ ok:false, error:{ message } }` carrying the same string. This
 * turns that raw string into an offending field + a human message so callers
 * can show it inline on a form control (or as a clean toast).
 *
 * Pure module — no React/antd/axios imports — so the model layer, the antd
 * adapter and `handleApiError` can all share it.
 */

export interface DuplicateKeyInfo {
    /** Best-effort offending field parsed from the dup key (e.g. "name"). */
    field?: string;
    /** The offending value, when parseable. */
    value?: string;
    /** Display-ready message. */
    message: string;
}

const DUPLICATE_RE = /e11000|duplicate key/i;

/** True when `text` looks like a Mongo duplicate-key error string. */
export function isDuplicateKeyMessage(text: unknown): boolean {
    return typeof text === "string" && DUPLICATE_RE.test(text);
}

/** Pull the first `field: value` pair out of a `dup key: { ... }` clause. */
function parseDupKey(raw: string): { field?: string; value?: string } {
    const block = raw.match(/dup key:\s*\{([^}]*)\}/i)?.[1];
    if (!block) return {};
    // e.g. `name: "foo"` / `dbKey: 'bar'` / `clientId_1_name_1: { ... }`
    const pair = block.match(/["']?([\w.]+)["']?\s*:\s*("?)([^,"]*)\2/);
    return { field: pair?.[1]?.trim() || undefined, value: pair?.[3]?.trim() || undefined };
}

/** Build a friendly message from a parsed dup-key field/value. */
function friendly(field?: string, value?: string, noun = "record"): string {
    if (field) {
        const what = value ? `"${value}"` : "that value";
        return `A ${noun} with ${field} ${what} already exists.`;
    }
    return `A ${noun} with the same value already exists.`;
}

/**
 * Parse a raw error string into duplicate-key info, or null if it isn't one.
 * `noun` tailors the message ("VAR service type", "batch", …).
 */
export function parseDuplicateKeyMessage(text: unknown, noun?: string): DuplicateKeyInfo | null {
    if (!isDuplicateKeyMessage(text)) return null;
    const { field, value } = parseDupKey(text as string);
    return { field, value, message: friendly(field, value, noun) };
}

/**
 * Detect a duplicate-key failure from an axios error (REST). Scans the common
 * message locations for the E11000 signature; treats a bare `409` as a
 * duplicate even when the body is opaque.
 */
export function duplicateKeyFromAxios(error: any, noun?: string): DuplicateKeyInfo | null {
    const data = error?.response?.data;
    const haystack = [data?.error, data?.message, data?.details, error?.message]
        .map((v) => (typeof v === "string" ? v : v == null ? "" : JSON.stringify(v)))
        .join(" ");
    const parsed = parseDuplicateKeyMessage(haystack, noun);
    if (parsed) return parsed;
    if (error?.response?.status === 409) {
        return { message: friendly(undefined, undefined, noun) };
    }
    return null;
}
